/**
 * Reporting basis — the DECLARED COLUMN FACT for flow (amount) columns (RB-6).
 *
 * A flow column ("Charge-Off Amount", "Recovery Amount", …) can be exported by a
 * bank's core three different ways, and the numbers are identical-looking in any
 * single month:
 *
 *   - `per_period`     — the amount is THIS month's activity (an event books once).
 *   - `running_total`  — a life-to-date cumulative that never resets.
 *   - `ytd`            — a cumulative that resets every January.
 *
 * Misreading a running total as per-period books a loan's loss N times (once per
 * snapshot), so the product NEVER guesses: the shape is inferred from the column's
 * own per-loan series (a suggestion), surfaced as a confirmable chip on the mapping
 * step, persisted on the org's field registry with provenance, and — when the data
 * is genuinely AMBIGUOUS (monotone non-decreasing series, which both cumulative
 * shapes and repeated same-sign increments produce; this includes a FLAT-REPEATED
 * nonzero value, the classic persisted life-to-date single charge-off) —
 * mapping/finalize BLOCKS until the customer answers. A user declaration is sticky;
 * a suggestion is re-derived and re-asked when new data contradicts it.
 *
 * The modeling derive normalizes every flow column to PER-PERIOD INCREMENTS at the
 * pivot (see cohort-facts-sql.ts `flowBasisNormalizedSelect`), so downstream math
 * sees exactly one shape.
 *
 * This module is the pure half: the extensible key registry, the vocabulary, and
 * the shape classifier over the per-loan series signals (the set-based SQL that
 * computes the signals lives in server/db/uploads/reporting-basis.ts).
 */

import type { StandardFieldKey } from './catalog';

/**
 * The flow columns whose reporting basis is a declared fact — extensible: adding a
 * key here (a numeric, event-shaped amount column) makes it detected, chip-surfaced,
 * gated, stored, and normalized with no further wiring. Keys must also be normalized
 * in the derive pivot (`FLOW_BASIS_PIVOT_COLUMNS` in cohort-facts-sql.ts asserts the
 * mapping stays total).
 */
export const REPORTING_BASIS_FIELD_KEYS = [
  'charge_off_amount',
  'net_charge_off_amount',
  'recovery_amount',
  'actual_principal_paid',
] as const satisfies readonly StandardFieldKey[];
export type ReportingBasisFieldKey = (typeof REPORTING_BASIS_FIELD_KEYS)[number];

/** The three reporting bases a flow column can carry. */
export const REPORTING_BASES = ['per_period', 'running_total', 'ytd'] as const;
export type ReportingBasis = (typeof REPORTING_BASES)[number];

/**
 * Where a stored basis came from. `suggested` = the detection prefill the customer
 * saved without editing (re-suggested when new data contradicts it); `user_declared`
 * = the customer explicitly chose it (sticky — never overwritten by a suggestion).
 */
export const REPORTING_BASIS_PROVENANCES = ['suggested', 'user_declared'] as const;
export type ReportingBasisProvenance = (typeof REPORTING_BASIS_PROVENANCES)[number];

/** Whether a Standard-field key is a reporting-basis (flow) column. */
export function isReportingBasisFieldKey(
  key: StandardFieldKey | string | null,
): key is ReportingBasisFieldKey {
  return key !== null && (REPORTING_BASIS_FIELD_KEYS as readonly string[]).includes(key);
}

/**
 * The per-loan series signals the detection SQL aggregates for one column (each
 * count is LOANS, not rows). "Interior" = a decrease between two observations in
 * the SAME calendar year (impossible for either cumulative shape); "year-boundary"
 * = a decrease where the calendar year advanced (the YTD reset — computed on the
 * year, not literally January, so a Feb-after-December observation still counts).
 */
export interface ReportingBasisSignals {
  /** Loans with >= 1 present value in the column. */
  readonly loanCount: number;
  /** Loans with >= 2 present values (only these carry any shape information). */
  readonly multiValueLoanCount: number;
  /** Loans whose series DECREASES somewhere within a calendar year. */
  readonly interiorDecreaseLoanCount: number;
  /**
   * Loans whose series is non-decreasing with > 1 distinct value and NO decrease
   * anywhere (the ambiguous cumulative-or-repeat-increment shape).
   */
  readonly monotoneMultiDistinctLoanCount: number;
  /**
   * Loans with ≥ 2 present values, ALL EQUAL and NONZERO (residual #3): the classic
   * persisted life-to-date single charge-off — the loan lost 800 once and the column
   * re-exports 800 every month after. Reading that as per_period books the same loss
   * N× (the original F67 bug, through a classifier hole: one distinct value is
   * trivially monotone but was not counted as ambiguous). An all-ZERO flat series
   * stays out (a running total of zero losses reads the same either way).
   */
  readonly flatRepeatedLoanCount: number;
  /** Loans whose ONLY decreases sit on a year boundary (the YTD reset pattern). */
  readonly yearBoundaryDecreaseLoanCount: number;
}

/** The detection verdicts. `ambiguous` is the only one that blocks. */
export type ReportingBasisVerdict = 'per_period' | 'ambiguous' | 'ytd';

/**
 * Minimum monotone-multi-distinct loans before the column is AMBIGUOUS — applied as
 * `>= min(this, multiValueLoanCount)` so a tiny book (1–2 loans, all monotone) still
 * blocks, while 1–2 monotone loans inside a larger book whose other multi-value
 * loans are flat/non-monotonic read as ordinary repeated events.
 */
export const REPORTING_BASIS_AMBIGUOUS_MIN_LOANS = 3;

/**
 * Classify a flow column's shape from its per-loan series signals. Pure — the
 * SQL computes the signals set-based; this encodes the RB-6 ruling:
 *
 *  1. No multi-value loans → every present value is a single event → unambiguous
 *     `per_period` (prefill, non-blocking).
 *  2. Any interior (same-year) decrease → cumulative shapes are impossible →
 *     unambiguous `per_period`.
 *  3. Decreases ONLY on year boundaries (and some exist) → the YTD reset pattern →
 *     suggest `ytd` (prefill, still confirmable, non-blocking).
 *  4. Monotone non-decreasing across enough loans → AMBIGUOUS (no prefill;
 *     mapping/finalize BLOCK until the customer answers). Two shapes count
 *     (residual #3 widened the trigger): > 1 distinct value (a growing cumulative
 *     or repeated same-sign increments), AND ≥ 2 present values all equal and
 *     nonzero (the persisted life-to-date single event — reading it per_period
 *     books the same loss once per snapshot).
 *  5. Otherwise (a few ambiguous-shaped loans in a book that is otherwise
 *     single-event / zero-flat) → `per_period`.
 */
export function classifyReportingBasisSignals(
  signals: ReportingBasisSignals,
): ReportingBasisVerdict {
  if (signals.multiValueLoanCount === 0) return 'per_period';
  if (signals.interiorDecreaseLoanCount > 0) return 'per_period';
  if (signals.yearBoundaryDecreaseLoanCount > 0) return 'ytd';
  const ambiguousFloor = Math.min(
    REPORTING_BASIS_AMBIGUOUS_MIN_LOANS,
    signals.multiValueLoanCount,
  );
  const ambiguousShapedLoans =
    signals.monotoneMultiDistinctLoanCount + signals.flatRepeatedLoanCount;
  if (ambiguousShapedLoans > 0 && ambiguousShapedLoans >= ambiguousFloor) {
    return 'ambiguous';
  }
  return 'per_period';
}

/**
 * The basis a verdict SUGGESTS as the chip prefill, or null when it must not
 * prefill (`ambiguous` — the customer answers before anything persists).
 */
export function suggestedBasisForVerdict(verdict: ReportingBasisVerdict): ReportingBasis | null {
  if (verdict === 'per_period') return 'per_period';
  if (verdict === 'ytd') return 'ytd';
  return null;
}

/**
 * Resolve the chip's effective prefill + blocking state from the stored declaration
 * and a fresh detection verdict (the ONE place the stickiness/contradiction rules
 * live — the endpoint, the finalize gate, and the client all read this):
 *
 *  - a `user_declared` basis is STICKY: prefill it, never block, ignore detection;
 *  - a `suggested` basis holds only while detection still agrees with it — a
 *    contradicting verdict re-suggests (falls through to the fresh detection);
 *  - no (surviving) stored value: prefill the verdict's suggestion; BLOCK only on
 *    `ambiguous` (RB-6: ytd is a non-blocking suggestion).
 */
export function resolveReportingBasisState(
  stored: { readonly basis: ReportingBasis; readonly provenance: ReportingBasisProvenance } | null,
  verdict: ReportingBasisVerdict,
): { readonly prefill: ReportingBasis | null; readonly blocking: boolean } {
  if (stored !== null && stored.provenance === 'user_declared') {
    return { prefill: stored.basis, blocking: false };
  }
  const suggestion = suggestedBasisForVerdict(verdict);
  if (stored !== null && stored.basis === suggestion) {
    return { prefill: stored.basis, blocking: false };
  }
  return { prefill: suggestion, blocking: suggestion === null };
}
